import { useEffect, useState, type ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  BookOpen,
  ChevronLeft,
  ClipboardCheck,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Settings2,
  Sparkles,
  Sun,
  Users,
  UserRound,
  X,
} from 'lucide-react'
import { BrandMark } from './BrandMark'
import { useAuthSession } from '../../lib/firebase'

interface AppShellProps {
  children: ReactNode
}

const navItems = [
  { to: '/', label: 'كل المحطات', shortLabel: 'المحطات', icon: LayoutDashboard, end: true },
  { to: '/review', label: 'قائمتي', shortLabel: 'قائمتي', icon: BookOpen },
  { to: '/exam', label: 'اختبار سريع', shortLabel: 'اختبار', icon: ClipboardCheck },
]

export function AppShell({ children }: AppShellProps) {
  const session = useAuthSession()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const saved = window.localStorage.getItem('mh-theme')
    if (saved === 'light' || saved === 'dark') return saved
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  })

  useEffect(() => {
    document.documentElement.dataset.theme = theme
    window.localStorage.setItem('mh-theme', theme)
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'dark' ? '#0d171f' : '#f3efe7')
  }, [theme])

  const pageLabel = location.pathname.startsWith('/stations/') || location.pathname.startsWith('/cases/')
    ? 'المحطة'
    : location.pathname.startsWith('/review')
      ? 'قائمتي'
      : location.pathname.startsWith('/exam')
        ? 'اختبار سريع'
        : location.pathname.startsWith('/admin')
          ? 'لوحة التحكم'
          : 'كل المحطات'

  function closeMobileSidebar() {
    setSidebarOpen(false)
  }

  return (
    <div className={`app-shell${collapsed ? ' app-shell--collapsed' : ''}`}>
      <div className={`sidebar-scrim${sidebarOpen ? ' sidebar-scrim--visible' : ''}`} onClick={closeMobileSidebar} aria-hidden="true" />
      <aside className={`app-sidebar${sidebarOpen ? ' app-sidebar--open' : ''}`} aria-label="التنقل الرئيسي">
        <div className="app-sidebar__topline">
          <BrandMark compact={collapsed} />
          <button className="icon-button sidebar-mobile-close" type="button" onClick={closeMobileSidebar} aria-label="إغلاق القائمة">
            <X size={19} />
          </button>
        </div>

        <div className="sidebar-section-label">التعلّم والمراجعة</div>
        <nav className="main-nav">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} className="main-nav__link" onClick={closeMobileSidebar}>
              <Icon size={19} strokeWidth={1.8} aria-hidden="true" />
              <span>{label}</span>
            </NavLink>
          ))}
          {session.isAdmin && (
            <>
              <NavLink to="/admin" className="main-nav__link main-nav__link--admin" onClick={closeMobileSidebar}>
                <Settings2 size={19} strokeWidth={1.8} aria-hidden="true" />
                <span>إدارة المحتوى</span>
                <span className="admin-dot" aria-label="مسؤول النظام" />
              </NavLink>
              <NavLink to="/admin/users" className="main-nav__link main-nav__link--admin" onClick={closeMobileSidebar}>
                <Users size={19} strokeWidth={1.8} aria-hidden="true" />
                <span>إدارة المستخدمين</span>
              </NavLink>
            </>
          )}
        </nav>

        <div className="sidebar-divider" />
        <div className="sidebar-quick-card">
          <div className="sidebar-quick-card__mark"><Sparkles size={16} /></div>
          <div>
            <strong>هل تريد مراجعة سريعة؟</strong>
            <span>أنشئ اختبارًا يناسب وقتك</span>
          </div>
          <ChevronLeft size={16} aria-hidden="true" />
        </div>

        <div className="app-sidebar__bottom">
          <button className="theme-toggle" type="button" onClick={() => setTheme((value) => value === 'dark' ? 'light' : 'dark')}>
            {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
            <span>{theme === 'dark' ? 'الوضع الفاتح' : 'الوضع الداكن'}</span>
          </button>
          <div className="sidebar-user">
            {session.user?.photoURL ? (
              <img src={session.user.photoURL} alt="" className="sidebar-user__avatar" />
            ) : (
              <span className="sidebar-user__avatar sidebar-user__avatar--fallback"><UserRound size={17} /></span>
            )}
            <span className="sidebar-user__copy">
              <strong>{session.user?.displayName ?? 'طالب'}</strong>
              <small>{session.isDemo ? 'النسخة التجريبية' : 'متصل الآن'}</small>
            </span>
            <button className="icon-button sidebar-user__logout" type="button" onClick={() => void session.signOut()} aria-label="تسجيل الخروج">
              <LogOut size={16} />
            </button>
          </div>
          <button className="collapse-button" type="button" onClick={() => setCollapsed((value) => !value)}>
            {collapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}
            <span>{collapsed ? 'توسيع القائمة' : 'تصغير القائمة'}</span>
          </button>
        </div>
      </aside>

      <main className="app-main">
        <header className="topbar">
          <div className="topbar__start">
            <button className="icon-button mobile-menu-button" type="button" onClick={() => setSidebarOpen(true)} aria-label="فتح القائمة">
              <Menu size={21} />
            </button>
            <div className="topbar__crumbs">
              <span>Medical Horizon</span>
              <ChevronLeft size={14} aria-hidden="true" />
              <strong>{pageLabel}</strong>
            </div>
          </div>
          <div className="topbar__actions">
            <button className="icon-button topbar-theme" type="button" onClick={() => setTheme((value) => value === 'dark' ? 'light' : 'dark')} aria-label={theme === 'dark' ? 'تفعيل الوضع الفاتح' : 'تفعيل الوضع الداكن'}>
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <span className="topbar__date">عملي الجراحة</span>
          </div>
        </header>
        <div className="app-content">{children}</div>
        <footer className="app-footer">
          <span>Medical Horizon · عملي الجراحة</span>
          <span>تعلّم · اختبر نفسك · راجع تقدمك</span>
        </footer>
      </main>

      <nav className="mobile-bottom-nav" aria-label="التنقل السريع">
        {navItems.map(({ to, shortLabel, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} className="mobile-bottom-nav__link">
            <Icon size={19} strokeWidth={1.8} />
            <span>{shortLabel}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  )
}
